import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Modal,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { md5 } from 'js-md5';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';

import { COLORS } from '@constants/colors';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { usePreventGameKeydownListening } from '@hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, {
  IBrandingHotspotApi,
  IUpdateBrandingHotspotData,
} from '@services/BrandingsService';
import FilesService from '@services/FilesService';

import { useEditorMode } from '../EditorModeContext';
import { startHotspotPlaceInUnreal } from '../hotspotsUnreal';
import { useInspectorStyles } from './styles';

interface HotspotInspectorProps {
  hotspotId: string;
}

type AnyHotspot = Record<string, unknown> & {
  brandingHotspotId?: string;
  name?: string;
  type?: string;
  isActive?: boolean;
  isInteractive?: boolean;
  position?: number[];
  data?: { url?: string; webBrowserUrl?: string; location?: string };
};

const VIDEO_URL_RE = /\.(mp4|webm|mov|m4v|ogg)$/;

const isVideoUrl = (url: string): boolean =>
  VIDEO_URL_RE.test(url.toLowerCase().split('?')[0]);

const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'];
const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const IMAGE_FILENAME_RE = /\.(png|jpe?g)$/;
const VIDEO_FILENAME_RE = /\.(mp4|webm|mov|m4v)$/;
const MODEL_FILENAME_RE = /\.glb$/;
const MODEL_MIME_TYPE = 'model/gltf-binary';

const getFilename = (url: string): string => {
  if (!url) {
    return '';
  }
  try {
    const parsed = new URL(url, window.location.href);
    return decodeURIComponent(parsed.pathname.split('/').pop() || '');
  } catch {
    return decodeURIComponent(url.split('/').pop() || '');
  }
};

const activeRowStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderRadius: '10px',
  backgroundColor: 'rgba(255, 255, 255, 0.045)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
};

const toggleHintStyles = {
  fontSize: '11px',
  lineHeight: 1.45,
  color: 'rgba(255, 255, 255, 0.55)',
  margin: '6px 2px 0',
};

const saveButtonStyles = {
  textTransform: 'none',
  backgroundColor: COLORS.MYTAVERSE_BLUE,
  color: COLORS.WHITE,
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  padding: '9px 14px',
  boxShadow: 'none',
  gap: '6px',
  '&:hover': { backgroundColor: '#3A46C1', boxShadow: 'none' },
};

const uploadModelBannerStyles = {
  display: 'flex',
  gap: '9px',
  padding: '11px 12px',
  borderRadius: '11px',
  backgroundColor: 'rgba(245, 166, 35, 0.14)',
  color: '#f6d49a',
  marginBottom: '8px',
};

const deleteButtonStyles = {
  textTransform: 'none',
  color: '#ff8585',
  borderColor: 'rgba(255, 255, 255, 0.18)',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 500,
  padding: '8px 14px',
  gap: '6px',
  '&:hover': {
    borderColor: '#FF6B6B',
    backgroundColor: 'rgba(255, 107, 107, 0.06)',
  },
};

/**
 * Renders the inspector for the selected branding hotspot.
 *
 * Every field autosaves through a PATCH and nudges UE with `SPOTS_UPDATED`. The
 * media card adapts to the hotspot variant: a `.glb` upload for 3D objects, an
 * image/video/URL picker for legacy 2D panels. 3D objects additionally expose
 * the world-interaction switch — off makes the object fully static.
 *
 * @param props.hotspotId - `brandingHotspotId` of the hotspot being edited.
 */
const HotspotInspector = ({ hotspotId }: HotspotInspectorProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const rootRef = usePreventGameKeydownListening<HTMLDivElement>();
  const { setAutosaveStatus, clearSelection } = useEditorMode();
  const {
    brandingHotspots,
    setBrandingHotspots,
    currentBrandingId,
    currentRoom,
    setPendingHotspotPlacement,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { showNotification } = useNotification();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const hotspot = useMemo<AnyHotspot | null>(() => {
    const source = Array.isArray(brandingHotspots) ? brandingHotspots : [];
    return (
      (source as AnyHotspot[]).find(
        (h) => h.brandingHotspotId === hotspotId,
      ) ?? null
    );
  }, [brandingHotspots, hotspotId]);

  const is3D = String(hotspot?.type ?? '').toUpperCase() === 'OBJECT_3D';
  const fileUrl = hotspot?.data?.url || '';
  const externalUrl = hotspot?.data?.webBrowserUrl || '';
  const effectiveUrl = fileUrl || externalUrl;
  const storedKind: 'IMAGE' | 'VIDEO' = isVideoUrl(effectiveUrl)
    ? 'VIDEO'
    : 'IMAGE';

  const [name, setName] = useState<string>(hotspot?.name ?? '');
  const [mediaKind, setMediaKind] = useState<'IMAGE' | 'VIDEO'>(storedKind);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const activeMediaUrl =
    effectiveUrl && (is3D || storedKind === mediaKind) ? effectiveUrl : '';
  const visibleExternalUrl = !fileUrl && activeMediaUrl ? externalUrl : '';

  const [mediaUrlInput, setMediaUrlInput] =
    useState<string>(visibleExternalUrl);

  useEffect(() => {
    setName(hotspot?.name ?? '');
  }, [hotspotId, hotspot?.name]);

  useEffect(() => {
    setMediaKind(isVideoUrl(effectiveUrl) ? 'VIDEO' : 'IMAGE');
  }, [hotspotId, effectiveUrl]);

  useEffect(() => {
    setMediaUrlInput(visibleExternalUrl);
  }, [hotspotId, visibleExternalUrl]);

  useEffect(() => {
    if (!activeMediaUrl) {
      setLightboxOpen(false);
    }
  }, [activeMediaUrl]);

  const refresh = useCallback(async () => {
    if (!currentBrandingId) {
      return;
    }
    const refreshed = await BrandingsService.getBrandingHotspots(
      currentBrandingId,
      { limit: 200 },
    );
    setBrandingHotspots(refreshed.hotspots ?? []);
  }, [currentBrandingId, setBrandingHotspots]);

  const persist = useCallback(
    async (
      patch: IUpdateBrandingHotspotData,
    ): Promise<IBrandingHotspotApi | null> => {
      if (!currentBrandingId || !hotspotId) {
        return null;
      }
      setAutosaveStatus('saving');
      try {
        const updated = await BrandingsService.patchBrandingHotspot(
          currentBrandingId,
          hotspotId,
          patch,
        );
        await refresh();
        setAutosaveStatus('saved');
        return updated;
      } catch (saveError) {
        console.error(
          `Failed to update hotspot: ${(saveError as Error)?.message}`,
          { brandingId: currentBrandingId, brandingHotspotId: hotspotId },
        );
        setAutosaveStatus('error');
        return null;
      } finally {
        sendMessageToUnreal({ action: 'SPOTS_UPDATED', spots: [] });
      }
    },
    [
      currentBrandingId,
      hotspotId,
      refresh,
      sendMessageToUnreal,
      setAutosaveStatus,
    ],
  );

  const handleNameBlur = useCallback(() => {
    if (name !== (hotspot?.name ?? '')) {
      persist({ name });
    }
  }, [name, hotspot?.name, persist]);

  const handleMediaUrlBlur = useCallback(() => {
    const trimmed = mediaUrlInput.trim();
    if (trimmed === visibleExternalUrl) {
      return;
    }
    persist({ data: { url: '', webBrowserUrl: trimmed } });
  }, [mediaUrlInput, visibleExternalUrl, persist]);

  const handleToggleActive = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      persist({ isActive: checked });
    },
    [persist],
  );

  const handleToggleInteractive = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      persist({ isInteractive: checked });
    },
    [persist],
  );

  const validateUploadFile = useCallback(
    (file: File): string | null => {
      const lowerName = file.name.toLowerCase();
      if (is3D) {
        if (MODEL_FILENAME_RE.test(lowerName) || file.type === MODEL_MIME_TYPE) {
          return null;
        }
        return translate(
          'editorMode.hotspotUnsupportedModel',
          'Unsupported format. Please upload a .glb 3D model file.',
        );
      }
      if (mediaKind === 'VIDEO') {
        if (
          ALLOWED_VIDEO_MIME_TYPES.includes(file.type) ||
          VIDEO_FILENAME_RE.test(lowerName)
        ) {
          return null;
        }
        return translate(
          'editorMode.hotspotUnsupportedVideo',
          'Unsupported format. Please upload an MP4, WebM, or MOV video file.',
        );
      }
      if (
        ALLOWED_IMAGE_MIME_TYPES.includes(file.type) ||
        IMAGE_FILENAME_RE.test(lowerName)
      ) {
        return null;
      }
      return translate(
        'editorMode.hotspotUnsupportedImage',
        'Unsupported format. Please upload a PNG or JPG image file.',
      );
    },
    [is3D, mediaKind, translate],
  );

  const isPlacedInWorld =
    Array.isArray(hotspot?.position) && (hotspot?.position?.length ?? 0) > 0;

  const handleUploadFiles = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0];
      if (!file || !currentBrandingId) {
        return;
      }
      const validationError = validateUploadFile(file);
      if (validationError) {
        showNotification({
          message: validationError,
          type: NOTIFICATION_TYPES.ERROR,
        });
        return;
      }
      setIsUploading(true);
      setUploadProgress(0);
      setAutosaveStatus('saving');
      try {
        let fileMd5: string | undefined;
        if (is3D) {
          fileMd5 = md5(await file.arrayBuffer());
        }
        const [uploaded] = await FilesService.uploadFilesWithProgress(
          [file],
          (progress: number) => setUploadProgress(progress),
        );
        if (uploaded?.url) {
          const updated = await persist({
            data: {
              url: uploaded.url,
              location: uploaded.location,
              ...(fileMd5 ? { md5: fileMd5 } : {}),
              webBrowserUrl: '',
            },
          });
          const relocatedUrl = updated?.data?.url;
          if (is3D && !isPlacedInWorld && relocatedUrl) {
            const relocatedMd5 = updated?.data?.md5 ?? fileMd5;
            setPendingHotspotPlacement({
              brandingHotspotId: hotspotId,
              isCreated: true,
            });
            startHotspotPlaceInUnreal(sendMessageToUnreal, '3D', {
              brandingHotspotId: hotspotId,
              ...(name || hotspot?.name
                ? { name: name || hotspot?.name }
                : {}),
              ...(currentRoom?.roomId ? { roomId: currentRoom.roomId } : {}),
              data: {
                url: relocatedUrl,
                ...(relocatedMd5 ? { md5: relocatedMd5 } : {}),
              },
            });
            showNotification({
              message: translate(
                'editorMode.hotspotPlacementStarted',
                'Model uploaded — now place the object in the world.',
              ),
              type: NOTIFICATION_TYPES.SUCCESS,
            });
          }
        }
      } catch (uploadError) {
        console.error(
          `Failed to upload hotspot media: ${(uploadError as Error)?.message}`,
          { brandingId: currentBrandingId, brandingHotspotId: hotspotId },
        );
        setAutosaveStatus('error');
      } finally {
        setIsUploading(false);
      }
    },
    [
      currentBrandingId,
      currentRoom,
      hotspot,
      hotspotId,
      is3D,
      isPlacedInWorld,
      name,
      persist,
      sendMessageToUnreal,
      setAutosaveStatus,
      setPendingHotspotPlacement,
      showNotification,
      translate,
      validateUploadFile,
    ],
  );

  const handleUploadClick = useCallback(() => {
    const input = fileInputRef.current;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }, []);

  const handleDeleteMedia = useCallback(() => {
    persist({ data: { url: '', webBrowserUrl: '' } });
  }, [persist]);

  const handleSaveHotspot = useCallback(() => {
    persist({
      name,
      isActive: hotspot?.isActive !== false,
      ...(is3D ? { isInteractive: hotspot?.isInteractive !== false } : {}),
    });
  }, [persist, name, hotspot?.isActive, hotspot?.isInteractive, is3D]);

  const handleDelete = useCallback(async () => {
    if (!currentBrandingId || !hotspotId) {
      return;
    }
    setAutosaveStatus('saving');
    try {
      await BrandingsService.deleteBrandingHotspot(currentBrandingId, hotspotId);
      await refresh();
      sendMessageToUnreal({ action: 'SPOTS_UPDATED', spots: [] });
      setAutosaveStatus('saved');
      clearSelection();
    } catch (deleteError) {
      console.error(
        `Failed to delete hotspot: ${(deleteError as Error)?.message}`,
        { brandingId: currentBrandingId, brandingHotspotId: hotspotId },
      );
      setAutosaveStatus('error');
    }
  }, [
    currentBrandingId,
    hotspotId,
    refresh,
    sendMessageToUnreal,
    setAutosaveStatus,
    clearSelection,
  ]);

  const mediaFilename = getFilename(activeMediaUrl);
  const showImageThumb = !is3D && !!activeMediaUrl && mediaKind === 'IMAGE';
  const showVideoThumb = !is3D && !!activeMediaUrl && mediaKind === 'VIDEO';
  const canZoomMedia = !is3D && !!activeMediaUrl;

  return (
    <Box ref={rootRef}>
      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {translate('editorMode.hotspotName', 'Name')}
        </Typography>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          size="small"
          fullWidth
          sx={styles.textField}
        />
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {is3D
            ? translate('editorMode.hotspotModel', '3D model')
            : mediaKind === 'VIDEO'
            ? translate('editorMode.video', 'Video')
            : translate('editorMode.hotspotImage', 'Image')}
        </Typography>
        {!is3D && (
          <Box sx={[styles.segmented, { marginBottom: '8px' }]}>
            <Box
              sx={[
                styles.segmentedItem,
                ...(mediaKind === 'IMAGE' ? [styles.segmentedItemActive] : []),
              ]}
              onClick={() => setMediaKind('IMAGE')}
            >
              {translate('editorMode.image', 'Image')}
            </Box>
            <Box
              sx={[
                styles.segmentedItem,
                ...(mediaKind === 'VIDEO' ? [styles.segmentedItemActive] : []),
              ]}
              onClick={() => setMediaKind('VIDEO')}
            >
              {translate('editorMode.video', 'Video')}
            </Box>
          </Box>
        )}
        {is3D && !effectiveUrl && (
          <Box sx={uploadModelBannerStyles}>
            <InfoOutlinedIcon
              sx={{ fontSize: 16, flexShrink: 0, marginTop: '1px' }}
            />
            <Typography sx={{ fontSize: '12px', lineHeight: 1.45 }}>
              {translate(
                'editorMode.hotspotUploadModelFirst',
                "Upload a 3D model (.glb) first — after the upload you'll place the object in the world.",
              )}
            </Typography>
          </Box>
        )}
        <Box sx={styles.mediaCard}>
          <Box sx={styles.compactRow}>
            <Box
              sx={[
                styles.compactThumb,
                ...(!showImageThumb && !showVideoThumb
                  ? [styles.spotThumbPlaceholder]
                  : []),
                ...(canZoomMedia ? [{ cursor: 'zoom-in' }] : []),
              ]}
              style={
                showImageThumb
                  ? { backgroundImage: `url("${activeMediaUrl}")` }
                  : undefined
              }
              onClick={canZoomMedia ? () => setLightboxOpen(true) : undefined}
            >
              {showVideoThumb && (
                <Box
                  component="video"
                  src={`${activeMediaUrl}#t=0.1`}
                  muted
                  playsInline
                  preload="metadata"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                  }}
                />
              )}
              {!showImageThumb &&
                !showVideoThumb &&
                (is3D ? (
                  <ViewInArOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                ) : mediaKind === 'VIDEO' ? (
                  <PlayArrowOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                ) : (
                  <ImageOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                ))}
              {canZoomMedia && (
                <Box sx={styles.thumbExpandBadge}>
                  <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
                </Box>
              )}
            </Box>

            <Box sx={styles.compactInfo}>
              <Typography sx={styles.compactTitle}>
                {mediaFilename ||
                  translate('editorMode.noMedia', 'No media set yet')}
              </Typography>
              <Typography sx={styles.compactSubtitle}>
                {is3D
                  ? translate('editorMode.hotspotModelKind', '3D model (.glb)')
                  : mediaKind === 'VIDEO'
                  ? translate('editorMode.video', 'Video')
                  : translate('editorMode.image', 'Image')}
              </Typography>
            </Box>

            <Box sx={styles.compactActions}>
              <IconButton
                size="small"
                sx={styles.iconSquareButton}
                onClick={handleUploadClick}
                disabled={isUploading}
                aria-label={translate('editorMode.uploadMedia', 'Upload')}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
              {!!activeMediaUrl && (
                <IconButton
                  size="small"
                  sx={[styles.iconSquareButton, styles.iconDangerButton]}
                  onClick={handleDeleteMedia}
                  disabled={isUploading}
                  aria-label={translate('editorMode.delete', 'Delete')}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {isUploading && (
            <Box sx={styles.progressBlock}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={styles.compactProgressBar}
              />
            </Box>
          )}

          <Box sx={styles.compactDivider} />

          <Box>
            <Typography sx={styles.fieldLabel}>
              {translate('editorMode.hotspotMediaUrl', 'Or paste a direct URL')}
            </Typography>
            <TextField
              value={mediaUrlInput}
              onChange={(e) => setMediaUrlInput(e.target.value)}
              onBlur={handleMediaUrlBlur}
              size="small"
              fullWidth
              placeholder="https://…"
              sx={styles.textField}
            />
          </Box>
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept={
            is3D
              ? '.glb,model/gltf-binary'
              : mediaKind === 'VIDEO'
              ? 'video/mp4,video/webm,video/quicktime'
              : 'image/png,image/jpeg'
          }
          style={{ display: 'none' }}
          onChange={(e) => handleUploadFiles(e.target.files)}
        />
      </Box>

      <Box sx={styles.section}>
        <Box sx={activeRowStyles}>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 500, color: COLORS.WHITE }}
          >
            {translate('editorMode.hotspotActive', 'Active')}
          </Typography>
          <Switch
            size="small"
            checked={hotspot?.isActive !== false}
            onChange={handleToggleActive}
            data-testid="hotspot-inspector-active-switch"
          />
        </Box>
      </Box>

      {is3D && (
        <Box sx={styles.section}>
          <Box sx={activeRowStyles}>
            <Typography
              sx={{ fontSize: '13px', fontWeight: 500, color: COLORS.WHITE }}
            >
              {translate(
                'editorMode.hotspotInteractive',
                'Interactive in world',
              )}
            </Typography>
            <Switch
              size="small"
              checked={hotspot?.isInteractive !== false}
              onChange={handleToggleInteractive}
              inputProps={{
                'aria-label': translate(
                  'editorMode.hotspotInteractive',
                  'Interactive in world',
                ),
              }}
              data-testid="hotspot-inspector-interactive-switch"
            />
          </Box>
          <Typography sx={toggleHintStyles}>
            {translate(
              'editorMode.hotspotInteractiveHint',
              'Off — the object becomes fully static: it cannot be clicked and is not highlighted on hover.',
            )}
          </Typography>
        </Box>
      )}

      <Box sx={styles.section}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSaveHotspot}
          sx={saveButtonStyles}
        >
          {translate('editorMode.saveHotspot', 'Save hotspot')}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleDelete}
          sx={deleteButtonStyles}
        >
          {translate('editorMode.deleteHotspot', 'Delete hotspot')}
        </Button>
      </Box>

      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        sx={styles.lightboxBackdrop}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
          }}
          onClick={() => setLightboxOpen(false)}
        >
          {activeMediaUrl && mediaKind === 'VIDEO' ? (
            <Box
              component="video"
              src={activeMediaUrl}
              controls
              autoPlay
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          ) : activeMediaUrl ? (
            <Box
              component="img"
              src={activeMediaUrl}
              alt={mediaFilename}
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          ) : null}
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={styles.lightboxClose}
            size="small"
            aria-label={translate('editorMode.close')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Modal>
    </Box>
  );
};

export default HotspotInspector;
